import * as Yup from 'yup';
import { useFormik } from 'formik';
import TableSelection from '../../common/DataTableViews/Auction/TableSelection';
import StatusSelectBox from '../../Sections/Admin/StatusSelectBox';
import Days from '../../Sections/Admin/DaysStatusSelectBox';
import { useEffect, useState } from 'react';
import moment from 'moment';
import { convertLocalTimeToUtc } from '../../../Helpers';
import dayjs from 'dayjs';
import type { TimePickerProps } from 'antd';
import { TimePicker } from 'antd';
import AuctionTypeSelection from '../../common/DataTableViews/Auction/AuctionTypeSelection';
import { useAuth } from '../../../modules/auth';

interface TAuction {
	isView?: Boolean;
	id?: string;
	tableId?: string;
	auctionType?: string;
	tablePrice: string;
	dineTime?: Date | string;
	dineDays: string[];
	days?: string[];
	status: string;
	endTime?: any;
	auctionStartDays: string[];
	auctionStartTime: any;
	auctionEndDays: string[];
	auctionEndTime: any;
	restaurantId?: string;
  listingType: string;
}

export const initialAuctionData: TAuction = {
	id: '',
	isView: false,
	tableId: '',
	dineTime: '',
	dineDays: [],
	tablePrice: '',
	days: [],
	status: 'Active',
	auctionType: '',
	endTime: '',
	auctionStartDays: [],
	auctionStartTime: '',
	auctionEndDays: [],
	auctionEndTime: '',
	listingType: 'AUCTION_ONLY'
};

const editAuctionSchema = Yup.object().shape({
	tableId: Yup.string().required('Table is required'),
	tablePrice: Yup.number()
			.when('listingType', {
				is: (listingType: string) => listingType !== 'BUY_IT_NOW_ONLY',
				then: (schema) => schema
					.min(10, 'Price must be greater than $10')
					.required('Starting bid is required')
					.typeError('Price must be a number')
					.max(20000, 'Value must be less than or equal to 20,000'),
				otherwise: (schema) => schema.notRequired()
			}),
	buyItNowPrice: Yup.number()
			.when('listingType', {
				is: (listingType: string) => listingType === 'BUY_IT_NOW_ONLY',
				then: (schema) => schema
					.min(10, 'Buy It Now price must be greater than $10')
					.required('Buy It Now price is required')
					.typeError('Buy It Now price must be a number')
					.max(20000, 'Value must be less than or equal to 20,000'),
				otherwise: (schema) => schema.notRequired()
			}),
	listingType: Yup.string()
			.required('Listing Type is required')
			.oneOf(['AUCTION_ONLY', 'AUCTION_BUY_IT_NOW', 'BUY_IT_NOW_ONLY'], 'Invalid listing type'),
	//dineTime: Yup.string().required('Dine In time is required'),
	days: Yup.array().min(1, 'At least one day must be selected'),
	status: Yup.string().required('status is required'),
	auctionType: Yup.string().required('Auction Type is required'),
	endTime: Yup.string().required('End Time is required'),
	auctionStartDays: Yup.array().min(1, 'Auction Start Day is required'),
	auctionStartTime: Yup.string().required('Auction Start Time is required'),
	//auctionEndDays: Yup.array().min(1, 'Auction End Day is required'),
	//auctionEndTime: Yup.string().required('Auction End Time is required'),
	dineDays: Yup.array()
		.min(1, 'Dine In Day is required')
		.test(
			'dine-day-after-auction-end',
			'Dine-in day must be on or after the auction end day',
			function (dineDays) {
				const { auctionEndDays } = this.parent;
				if (!dineDays || !auctionEndDays) return true;

				const daysOrder = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
				const dineDayIndex = daysOrder.indexOf(dineDays[0]);
				const endDayIndex = daysOrder.indexOf(auctionEndDays[0]);

				return dineDayIndex >= endDayIndex;
			}
		),
	dineTime: Yup.string()
		.required('Dine In Time is required')
		.test(
			'dine-time-after-auction-end',
			'Dine-in time must be on or after the auction end time when days are the same',
			function (dineTime) {
				const { dineDays, auctionEndDays, auctionEndTime } = this.parent;
				if (!dineTime || !auctionEndTime || !dineDays || !auctionEndDays) return true;

				// Solo validar si los días son iguales
				if (dineDays[0] === auctionEndDays[0]) {
					const dineMoment = moment(dineTime, 'h:mm A');
					const auctionEndMoment = moment(auctionEndTime, 'h:mm A');
					return dineMoment.isSameOrAfter(auctionEndMoment);
				}
				return true;
			}
		),
	auctionEndDays: Yup.array()
		.test(
			'end-day-after-start',
			'Auction end day must be after the start day',
			function (endDays) {
				const { auctionStartDays } = this.parent;
				if (!endDays || !auctionStartDays) return true;

				const daysOrder = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
				const startDayIndex = daysOrder.indexOf(auctionStartDays[0]);
				const endDayIndex = daysOrder.indexOf(endDays[0]);

				return endDayIndex >= startDayIndex;
			}
		),
	auctionEndTime: Yup.string()
		.test(
			'end-time-after-start',
			'Auction end time must be after the start time when days are the same',
			function (endTime) {
				const { auctionStartDays, auctionEndDays, auctionStartTime } = this.parent;
				if (!endTime || !auctionStartTime || !auctionStartDays || !auctionEndDays) return true;

				// Solo validar si los días son iguales
				if (auctionStartDays[0] === auctionEndDays[0]) {
					const startMoment = moment(auctionStartTime, 'h:mm A');
					const endMoment = moment(endTime, 'h:mm A');
					return endMoment.isAfter(startMoment);
				}
				return true;
			}
		)
});

const RecurringAuctionBody = ({
	auctionData,
	handleSaveCallback,
	handleCancelCallback,
}: {
	auctionData: TAuction | any | null;
	handleSaveCallback: (value: any) => void;
	handleCancelCallback: () => void;
}) => {
	const [auctionDataByApi, setAuctionDataByApi] = useState<any>();
	const { auctionModule } = useAuth();

	const [isLoading, setIsLoading] = useState(false);

	const getRecurringAuctionDetails = async () => {
		if (!auctionData?.id) return;

		setIsLoading(true);
		try {
			const result = await auctionModule?.getSingleRecurringAuctions(auctionData.id);
			console.log(result)

			if (!result?.success) {
				console.error("Failed to fetch auction details:", result?.message);
				return;
			}

			// Verificación adicional
			if (result.data?.id !== auctionData.id) {
				console.error("Critical ID mismatch - received:", result.data?.id, "expected:", auctionData.id);
				return;
			}

			setAuctionDataByApi(result.data);
		} catch (error) {
			console.error("Error in getRecurringAuctionDetails:", error);
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {

		let isMounted = true;

		const fetchData = async () => {
			if (isMounted && auctionData?.id) {
				await getRecurringAuctionDetails();
			}
		};

		fetchData();

		return () => {
			isMounted = false;
		};
	}, [auctionData?.id]);


	useEffect(() => {
		if (auctionDataByApi && !formik.dirty && auctionDataByApi.id === auctionData?.id) {

			const formatTime = (timeString: string) =>
				moment(timeString, 'HH:mm:ss').format('h:mm A');

			formik.setValues({
				...formik.values,
				//auctionStartDate: parseDate(auctionDataByApi.auctionStartDate),
				auctionStartTime: formatTime(auctionDataByApi.auctionStartTime),
				//auctionEndDate: parseDate(auctionDataByApi.auctionEndDate),
				auctionEndTime: formatTime(auctionDataByApi.auctionEndTime),
				auctionStartDays: auctionDataByApi?.auctionStartDays || [],
				auctionEndDays: auctionDataByApi?.auctionEndDays || [],
				dineDays: auctionDataByApi?.dineDays || [],
				listingType: auctionDataByApi?.listingType,
				buyItNowPrice: auctionDataByApi?.buyItNowPrice || ""
			});

		}
	}, [auctionDataByApi, auctionData?.id]);


	const formik = useFormik({
		initialValues: auctionData || initialAuctionData,
		validationSchema: editAuctionSchema,
		onSubmit: async (value) => {
			const formatTimeTo24H = (time: string) => {
				if (!time) return '';
				return moment(time, ["h:mm A", "HH:mm:ss"]).format("HH:mm:ss");
			};

			formik.setSubmitting(true);

			const currentDate = moment().format('YYYY-MM-DD');

			let auctionData: any = {
				tableId: value.tableId,
				//dineTime: `${currentDate} ${formatTimeTo24H(value.dineTime)}`, // "YYYY-MM-DD HH:mm:ss"
				dineTime: convertLocalTimeToUtc(value.dineTime, moment().format('YYYY-MM-DD')),
				status: value.status,
				days: value.days,
				timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
				auctionTypeId: value?.auctionType,
				endTime: `${currentDate} ${formatTimeTo24H(value.endTime)}`,
				auctionStartDays: value?.auctionStartDays,
				auctionStartTime: formatTimeTo24H(value.auctionStartTime),
				auctionEndDays: value?.auctionEndDays,
				auctionEndTime: formatTimeTo24H(value.auctionEndTime),
				dineDays: value?.dineDays,
				restaurantId: value.restaurantId,
				listingType: value.listingType
			};

			if (value.listingType !== 'BUY_IT_NOW_ONLY') {
				auctionData.price = value.tablePrice;
			}

			if (value.listingType === 'BUY_IT_NOW_ONLY') {
				auctionData.buyItNowPrice = value.buyItNowPrice;
			} else if (value.listingType === 'AUCTION_BUY_IT_NOW') {
				auctionData.buyItNowPrice = parseFloat(value.tablePrice) * 1.25;
			}

			console.log("Sending data:", auctionData);
			await handleSaveCallback(auctionData);
			formik.setSubmitting(false);
		},
	});

	const [todayDay, setTodayDay] = useState<any>(null);
	const [errorMessage, setErrorMessage] = useState('');
	const currentTimePlus4Hours = moment().add(4, 'hours').format('HH:mm');

	useEffect(() => {
		const today = new Date().toLocaleDateString('en-US', { weekday: 'long' });
		setTodayDay(today);
	}, []);

	function getCurrentTime() {
		const currentDate = new Date();
		const hours = currentDate.getHours();
		const minutes = currentDate.getMinutes();
		return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
	}


	const handleDineTimeChange = (value: string) => {
		const selectedTime = dayjs(value, 'h:mm A');
		const currentTime = dayjs();
		const currentTimePlus4Hours = currentTime.add(4, 'hours');
		const selectedDays = formik.values.days || [];
		const today = currentTime.format('dddd');

		if (selectedDays.length === 0) {
			setErrorMessage('Please select days first.');
		} else if (
			currentTime.isBefore(selectedTime) &&
			selectedTime.isBefore(currentTimePlus4Hours) &&
			selectedDays.includes(today)
		) {
			setErrorMessage(
				`Auction won't go live for today as there is less than 4-hour gap between current and Dine-In time.`
			);
		} else {
			setErrorMessage('');
		}
		formik.setFieldValue('dineTime', value);
		const bidsEndAtTime = selectedTime.subtract(4, 'hours').format('h:mm A');
		formik.setFieldValue('endTime', bidsEndAtTime);
	};

	const onChange: TimePickerProps['onChange'] = (time: any, timeString: any) => {
		handleDineTimeChange(timeString);
	};

	const validateAllDaysAndTimes = (
		startDays: string[],
		endDays: string[],
		dineDays: string[],
		startTime: string | null,
		endTime: string | null,
		dineTime: string | null
	): string | null => {
		const daysOrder = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

		if (!startDays?.length || !endDays?.length || !dineDays?.length) return null;

		// Validate that End Day is after Start Day
		if (daysOrder.indexOf(endDays[0]) < daysOrder.indexOf(startDays[0])) {
			return 'Auction end day must be after the start day.';
		}

		// If it is the same day, validate that the end time is after the start time
		if (endDays[0] === startDays[0]) {
			const startMoment = moment(startTime, 'HH:mm:ss');
			const endMoment = moment(endTime, 'HH:mm:ss');

			if (!startMoment.isValid() || !endMoment.isValid()) {
				return 'Please enter valid times for auction start and end.';
			}

			if (endMoment.isSameOrBefore(startMoment)) {
				return 'Auction end time must be after the start time.';
			}
		}

		// Validate that Dine In Day is after Auction Start Day
		if (daysOrder.indexOf(dineDays[0]) < daysOrder.indexOf(startDays[0])) {
			return 'Dine-in day must be after the auction start.';
		}

		// Validate that Dine In Day is the same as or after Auction End Day
		if (daysOrder.indexOf(dineDays[0]) < daysOrder.indexOf(endDays[0])) {
			return 'Dine-in day must be on or after the auction end day.';
		}


		if (dineDays[0] === endDays[0]) {
			const endMoment = moment(endTime, 'HH:mm:ss');
			const dineMoment = moment(dineTime, 'HH:mm:ss');

			if (dineMoment.isBefore(endMoment)) {
				return 'Dine-in time must be on or after the auction end time.';
			}
		}

		return null;
	};
	// const handleDaysChange = (selectedDays: string[], fieldName: string) => {

	// 	if (fieldName === 'dineDays' && selectedDays.length > 1) {
	// 		selectedDays = [selectedDays[selectedDays.length - 1]]; // Take only the last selected
	// 	}

	// 	if (fieldName === 'auctionStartDays' && selectedDays.length > 1) {
	// 		selectedDays = [selectedDays[selectedDays.length - 1]]; // Take only the last selected
	// 	}

	// 	if (fieldName === 'auctionEndDays' && selectedDays.length > 1) {
	// 		selectedDays = [selectedDays[selectedDays.length - 1]]; // Take only the last selected
	// 	}

	// 	formik.setFieldValue(fieldName, selectedDays);


	// 	if (fieldName === 'dineDays') {
	// 		formik.setFieldValue('days', selectedDays);
	// 	}


	// 	const error = validateAllDaysAndTimes(
	// 		fieldName === 'auctionStartDays' ? selectedDays : formik.values.auctionStartDays,
	// 		fieldName === 'auctionEndDays' ? selectedDays : formik.values.auctionEndDays,
	// 		fieldName === 'dineDays' ? selectedDays : formik.values.dineDays,
	// 		formik.values.auctionStartTime,
	// 		formik.values.auctionEndTime,
	// 		formik.values.dineTime
	// 	);

	// 	setErrorMessage(error || '');
	// };
	const handleDaysChange = (selectedDays: string[], fieldName: string) => {
		// Limitar a un solo día seleccionado para ciertos campos
		if (['dineDays', 'auctionStartDays', 'auctionEndDays'].includes(fieldName)) {
			selectedDays = selectedDays.length > 1 ? [selectedDays[selectedDays.length - 1]] : selectedDays;
		}

		formik.setFieldValue(fieldName, selectedDays);

		// Si es dineDays, actualizar también days
		if (fieldName === 'dineDays') {
			formik.setFieldValue('days', selectedDays);
		}

		// Forzar validación después del cambio
		formik.validateForm();
	};

	const listingTypeOptions = [
		{ value: 'AUCTION_ONLY', label: 'Auction Only' },
		{ value: 'AUCTION_BUY_IT_NOW', label: 'Auction + Buy It Now' },
		{ value: 'BUY_IT_NOW_ONLY', label: 'Buy It Now Only' }
	];

	return (
		<>
			<form id='kt_modal_add_user_form' className='form' onSubmit={formik.handleSubmit} noValidate>
				<div className='d-flex flex-column me-n7 pe-7' id='kt_modal_add_user_scroll'>
				  <div className='fv-row mb-4'>
							<label className='required fw-bold fs-6 mb-2'>Listing Type</label>
							<select
								className='form-select form-select-solid'
								{...formik.getFieldProps('listingType')}
								disabled={auctionData?.isView === true}
							>
								{listingTypeOptions.map((option) => (
									<option key={option.value} value={option.value}>
										{option.label}
									</option>
								))}
							</select>
							{formik.touched.listingType && formik.errors.listingType && (
								<div className='fv-plugins-message-container'>
									<div className='fv-help-block'>
										<span role='alert'>{formik.errors.listingType as string}</span>
									</div>
								</div>
							)}
						</div>

					<div className='fv-row row '>
						<div className='col-md-6 mb-4'>
							<label className='required fw-bold fs-6 mb-2'>Table</label>
							<TableSelection
								onblur={() => {
									// formik.setFieldTouched('tableId', true);
								}}
								as='select'
								{...formik.getFieldProps('tableId')}
								onChange={(value) => {
									formik.setFieldValue('tableId', value.value);
								}}
								name='tableId'
								value={formik.getFieldProps('tableId').value}
								isDisabled={auctionData?.isView === true || auctionData}
							/>

							{formik.touched.tableId && formik.errors.tableId && (
								<div className='fv-plugins-message-container'>
									<div className='fv-help-block'>
										<span role='alert'>{formik.errors.tableId as string}</span>
									</div>
								</div>
							)}
						</div>
						{formik.values.listingType !== 'BUY_IT_NOW_ONLY' ? (
							<div className='col-md-6 mb-4'>
								<label className='required fw-bold fs-6 mb-2'>Starting Bid</label>
								<div className='input-group'>
									<div className='input-group-prepend'>
										<span className='input-group-text input-prepand-text '>$</span>
									</div>
									<input
										placeholder='Starting Bid'
										{...formik.getFieldProps('tablePrice')}
										type='number'
										name='tablePrice'
										className='form-control form-control-solid mb-3 mb-lg-0'
										autoComplete='off'
										disabled={formik.isSubmitting}
										onKeyDown={(evt) => (evt.key === 'e' || evt.key === '-') && evt.preventDefault()}
									/>
								</div>
								{formik.touched.tablePrice && formik.errors.tablePrice && (
									<div className='fv-plugins-message-container'>
										<div className='fv-help-block'>
											<span role='alert'>{formik.errors.tablePrice as string}</span>
										</div>
									</div>
								)}
							</div>
						): (
				  <div className='col-md-6 mb-4'>
							<label className='required fw-bold fs-6 mb-2'>Buy It Now Price</label>
							<div className='input-group'>
								<div className='input-group-prepend'>
									<span className='input-group-text input-prepand-text '>$</span>
								</div>
								<input
									placeholder='Buy It Now Price'
									{...formik.getFieldProps('buyItNowPrice')}
									type='number'
									name='buyItNowPrice'
									className='form-control form-control-solid mb-3 mb-lg-0'
									autoComplete='off'
									disabled={formik.isSubmitting}
									onKeyDown={(evt) => (evt.key === 'e' || evt.key === '-') && evt.preventDefault()}
								/>
							</div>
							{formik.touched.buyItNowPrice && formik.errors.buyItNowPrice && (
								<div className='fv-plugins-message-container'>
									<div className='fv-help-block'>
										<span role='alert'>{formik.errors.buyItNowPrice as string}</span>
									</div>
								</div>
							)}
						</div>
						)}
					</div>
					<div className='fv-row row mt-4'>
						<div className='col-md-6 mb-4 '>
							<label className='required fw-bold fs-6 mb-2'>Auction Start Day</label>
							<Days
								onChange={(values) => handleDaysChange(values, 'auctionStartDays')}
								isView={auctionData?.isView === true}
								value={formik.getFieldProps('auctionStartDays').value || []}
								id='auctionStartDays'
							/>
							{formik.touched.auctionStartDays && formik.errors.auctionStartDays && (
								<div className='fv-plugins-message-container'>
									<div className='fv-help-block'>
										<span role='alert'>{formik.errors.auctionStartDays as string}</span>
									</div>
								</div>
							)}
						</div>
						<div className='col-md-6 mb-4'>
							<label className='required fw-bold fs-6 mb-2'>Auction Start Time</label>
							<TimePicker
								use12Hours
								value={formik.values.auctionStartTime ? dayjs(formik.values.auctionStartTime, 'h:mm A') : null}
								format='h:mm A'
								allowClear={false}
								needConfirm={false}
								disabled={auctionData?.isView === true}
								id='auctionRecurringStartTime'
								onChange={(time, timeString) => {
									formik.setFieldValue('auctionStartTime', timeString);
								}}
								className='form-control form-control-lg form-control-solid'
							/>
							{formik.touched.auctionStartTime && formik.errors.auctionStartTime && (
								<div className='fv-plugins-message-container'>
									<div className='fv-help-block'>
										<span role='alert'>{formik.errors.auctionStartTime as string}</span>
									</div>
								</div>
							)}
						</div>
					</div>
					<div className='fv-row row'>
						<div className='col-md-6 mb-4'>
							<label className='required fw-bold fs-6 mb-2'>Auction End Day</label>
							<Days
								onChange={(values) => handleDaysChange(values, 'auctionEndDays')}
								isView={auctionData?.isView === true}
								value={formik.getFieldProps('auctionEndDays').value || []}
								id='auctionEndDays'
							/>
							{formik.touched.auctionEndDays && formik.errors.auctionEndDays && (
								<div className='fv-plugins-message-container'>
									<div className='fv-help-block'>
										<span role='alert'>{formik.errors.auctionEndDays as string}</span>
									</div>
								</div>
							)}
						</div>
						<div className='col-md-6 mb-4'>
							<label className='required fw-bold fs-6 mb-2'>Auction End Time</label>
							<TimePicker
								use12Hours
								value={formik.values.auctionEndTime ? dayjs(formik.values.auctionEndTime, 'h:mm A') : null}
								format='h:mm A'
								allowClear={false}
								needConfirm={false}
								disabled={auctionData?.isView === true}
								id='auctionRecurringEndTime'
								onChange={(time, timeString) => {
									formik.setFieldValue('auctionEndTime', timeString);
								}}
								className='form-control form-control-lg form-control-solid'
							/>
							{formik.touched.auctionEndTime && formik.errors.auctionEndTime && (
								<div className='fv-plugins-message-container'>
									<div className='fv-help-block'>
										<span role='alert'>{formik.errors.auctionEndTime as string}</span>
									</div>
								</div>
							)}
						</div>
					</div>
					<div className='fv-row row'>
						<div className='col-md-6 mb-4'>
							<label className='required fw-bold fs-6 mb-2'>Dine In Day</label>
							<Days
								onChange={(values) => handleDaysChange(values, 'dineDays')}
								isView={auctionData?.isView === true}
								value={formik.getFieldProps('dineDays').value || []}
								id='dineDays'
							/>
							{formik.touched.dineDays && formik.errors.dineDays && (
								<div className='fv-plugins-message-container'>
									<div className='fv-help-block'>
										<span role='alert'>{formik.errors.dineDays as string}</span>
									</div>
								</div>
							)}
						</div>
						<div className='col-md-6 mb-4'>
							<label className='required fw-bold fs-6 mb-2'>Dine In Time</label>
							<TimePicker
								use12Hours
								defaultValue={
									(formik?.values?.dineTime && dayjs(formik?.values?.dineTime, 'h:mm A')) || null
								}
								format='h:mm A'
								allowClear={false}
								needConfirm={false}
								disabled={auctionData?.isView === true}
								id='auctionRecurringDineTime'
								onChange={onChange}
								className='form-control form-control-lg form-control-solid !h-[50px]'
							/>
							{formik.touched.dineTime && formik.errors.dineTime && (
								<div className='fv-plugins-message-container'>
									<div className='fv-help-block'>
										<span role='alert'>{formik.errors.dineTime as string}</span>
									</div>
								</div>
							)}
						</div>


					</div>


					<div className='fv-row row'>
						{/* <div className='col-md-6 mb-4'>
							<label className='required fw-bold fs-6 mb-2'>End Time</label>
							<TimePicker
								use12Hours
								value={formik?.values?.endTime ? dayjs(formik?.values?.endTime, 'h:mm A') : null}
								format='h:mm A'
								allowClear={false}
								needConfirm={false}
								onChange={(time, timeString: any) => {
									if (time) {
										formik.setFieldValue('endTime', timeString);
									}
								}}
								disabled={auctionData?.isView === true}
								className='form-control form-control-lg form-control-solid'
							/>

							{formik.touched.endTime && formik.errors.endTime && (
								<div className='fv-plugins-message-container'>
									<div className='fv-help-block'>
										<span role='alert'>{formik.errors.endTime as string}</span>
									</div>
								</div>
							)}
						</div> */}
						<div className='col-md-6 mb-4'>
							<label className='required fw-bold fs-6 mb-2'>Seating Type</label>
							<AuctionTypeSelection
								onblur={() => {
									formik.setFieldTouched('auctionType', true);
								}}
								as='select'
								{...formik.getFieldProps('auctionType')}
								onChange={(value) => {
									formik.setFieldValue('auctionType', value.value);
								}}
								name='auctionType'
								value={formik.getFieldProps('auctionType').value}
								isDisabled={auctionData?.isView === true}
								className=''
							/>
							{formik.touched.auctionType && formik.errors.auctionType && (
								<div className='fv-plugins-message-container'>
									<div className='fv-help-block'>
										<span role='alert'>{formik.errors.auctionType as string}</span>
									</div>
								</div>
							)}
						</div>
						<div className='col-md-6 mb-4'>
							<label className='required fw-bold fs-6 mb-2'>Status</label>
							<StatusSelectBox
								onChange={(e) => {
									formik.setFieldValue('status', e.target.value);
								}}
								value={formik.getFieldProps('status').value}
								className='p-4'
								disabled={auctionData?.isView === true}
							/>
							{formik.touched.status && formik.errors.status && (
								<div className='fv-plugins-message-container'>
									<div className='fv-help-block'>
										<span role='alert'>{formik.errors.status as string}</span>
									</div>
								</div>
							)}
						</div>
					</div>
					<div className='text-center my-4'>
						{' '}
						{errorMessage && (
							<div className='fv-plugins-message-container'>
								<div className='fv-help-block'>
									<span role='alert'>Note: {errorMessage}</span>
								</div>
							</div>
						)}
					</div>
					<div className='text-center'>
						{auctionData?.isView !== true && (
							<>
								<button
									type='reset'
									onClick={handleCancelCallback}
									className='btn btn-light me-3'
									disabled={formik.isSubmitting}
								>
									Discard
								</button>

								<button
									type='submit'
									className='btn btn-primary'
									disabled={formik.isSubmitting}
								>
									{!formik.isSubmitting && 'Submit'}
									{formik.isSubmitting && (
										<span className='indicator-progress' style={{ display: 'block' }}>
											Please wait...{' '}
											<span className='spinner-border spinner-border-sm align-middle ms-2'></span>
										</span>
									)}
								</button>
							</>
						)}
					</div>
				</div>
			</form>
		</>
	);
};

export default RecurringAuctionBody;
